/**
 * Auto-escaping HTML templates.
 *
 *   html`<p class="${cls}">${userText}</p>`
 *
 * Every interpolated value is escaped unless it is itself the result of html`` (or raw()).
 * Arrays are flattened, and null / undefined / false render as nothing. Because escaping is
 * the default, data that comes from the API can never become markup by accident.
 *
 * Rules for template authors:
 *  - always quote attributes:  class="${x}"  (never class=${x})
 *  - never build URLs for href/src by hand; use safeUrl()
 *  - never put interpolated values inside <script>, <style> or on* attributes
 */

class Safe {
  constructor(value) {
    this.value = value;
  }

  toString() {
    return this.value;
  }
}

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '`': '&#96;' };

/** Escape a value for use in element text or in a quoted attribute. */
export function esc(value) {
  return String(value ?? '').replace(/[&<>"'`]/g, (c) => ESCAPES[c]);
}

/** Mark a string as already-safe markup. Only use for trusted constants (e.g. SVG path data). */
export function raw(value) {
  return new Safe(String(value ?? ''));
}

export function isSafe(value) {
  return value instanceof Safe;
}

function render(value) {
  if (value == null || value === false || value === true) return '';
  if (value instanceof Safe) return value.value;
  if (Array.isArray(value)) return value.map(render).join('');
  return esc(value);
}

/** A quoted data-* or aria-* attribute whose whole value is one interpolation:  data-on="${...}" */
const STATE_ATTRIBUTE = /\b(?:data|aria)-[\w-]+\s*=\s*"$/;

/**
 * Tagged template that escapes interpolations. Returns Safe markup.
 *
 * A lone boolean inside a data-* or aria-* attribute renders as "true" or "false" (aria-expanded="${open}",
 * data-on="${!item.active}"). Everywhere else true / false / null render as nothing, so the usual
 * `${condition && html`...`}` idiom works.
 */
export function html(strings, ...values) {
  let out = strings[0];
  for (let i = 0; i < values.length; i += 1) {
    const value = values[i];
    if (typeof value === 'boolean' && strings[i + 1].startsWith('"') && STATE_ATTRIBUTE.test(out)) out += String(value);
    else out += render(value);
    out += strings[i + 1];
  }
  return new Safe(out);
}

/** Join an array of Safe/strings with a separator (also safe). */
export function join(items, separator = '') {
  return new Safe(items.map(render).join(separator));
}

/**
 * Allow only http(s), mailto and tel links (and same-site paths). Anything else, such as
 * `javascript:` or `data:`, becomes '#'. Use for every href that includes user-provided data.
 */
export function safeUrl(url) {
  const value = String(url ?? '').trim();
  if (!value) return '#';
  if (value.startsWith('/') && !value.startsWith('//')) return value;
  try {
    const parsed = new URL(value);
    return ['http:', 'https:', 'mailto:', 'tel:'].includes(parsed.protocol) ? parsed.href : '#';
  } catch {
    return '#';
  }
}

/** Turn Safe or string into a plain string for innerHTML. */
export function toHtml(value) {
  return render(value);
}
